import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';

const Login = () => {
  const navigate = useNavigate();
  const { login, signup, signInWithGoogle } = useAuth();
  const [isSignup, setIsSignup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isSignup) {
        if (!formData.displayName.trim()) {
          setError('Display name is required');
          setLoading(false);
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        await signup(formData.email, formData.password, formData.displayName);
      } else {
        await login(formData.email, formData.password);
      }
      navigate('/');
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError(null);

    try {
      await signInWithGoogle();
      navigate('/');
    } catch (err) {
      setError(err.message || 'Google Sign-In failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="w-full max-w-[440px] bg-white rounded-2xl shadow-lg border border-neutral-200 p-8 md:p-10 relative z-10">
      {/* Header */}
      <div className="mb-8 text-center">
        <h1 className="font-display text-3xl text-neutral-900 mb-2">
          {isSignup ? 'Create Account' : 'Welcome back'}
        </h1>
        <p className="text-neutral-500 text-sm">
          {isSignup ? 'Sign up to start managing your pipeline.' : 'Log in to manage your pipeline and clients.'}
        </p>
      </div>

      {/* Alpha Badge */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/10 border border-secondary/20">
          <span className="material-symbols-outlined text-secondary text-[16px]">
            science
          </span>
          <span className="text-xs font-medium text-secondary">
            Alpha Version
          </span>
        </div>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-4">
        {isSignup && (
          <div className="flex flex-col gap-1.5">
            <label className="text-neutral-700 text-sm font-medium" htmlFor="displayName">
              Full Name
            </label>
            <div className="relative">
              <input
                className="w-full rounded-xl bg-neutral-50 border border-neutral-200 text-neutral-900 placeholder-neutral-400 focus:ring-2 focus:ring-primary/20 focus:border-primary p-3.5 pl-11 text-sm transition-all"
                id="displayName"
                name="displayName"
                placeholder="John Doe"
                type="text"
                value={formData.displayName}
                onChange={handleChange}
                required={isSignup}
              />
              <span
                className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none"
                style={{ fontSize: '20px' }}
              >
                person
              </span>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <label className="text-neutral-700 text-sm font-medium" htmlFor="email">
            Email
          </label>
          <div className="relative">
            <input
              className="w-full rounded-xl bg-neutral-50 border border-neutral-200 text-neutral-900 placeholder-neutral-400 focus:ring-2 focus:ring-primary/20 focus:border-primary p-3.5 pl-11 text-sm transition-all"
              id="email"
              name="email"
              placeholder="name@example.com"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
            <span
              className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none"
              style={{ fontSize: '20px' }}
            >
              mail
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex justify-between items-center">
            <label className="text-neutral-700 text-sm font-medium" htmlFor="password">
              Password
            </label>
            {!isSignup && (
              <button type="button" className="text-primary hover:text-primary-600 text-xs font-medium hover:underline transition-colors">
                Forgot password?
              </button>
            )}
          </div>
          <div className="relative">
            <input
              className="w-full rounded-xl bg-neutral-50 border border-neutral-200 text-neutral-900 placeholder-neutral-400 focus:ring-2 focus:ring-primary/20 focus:border-primary p-3.5 pl-11 pr-11 text-sm transition-all"
              id="password"
              name="password"
              placeholder="Enter your password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
            />
            <span
              className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none"
              style={{ fontSize: '20px' }}
            >
              lock
            </span>
            <button
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-primary transition-colors cursor-pointer"
              type="button"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                {showPassword ? 'visibility_off' : 'visibility'}
              </span>
            </button>
          </div>
        </div>

        {isSignup && (
          <div className="flex flex-col gap-1.5">
            <label className="text-neutral-700 text-sm font-medium" htmlFor="confirmPassword">
              Confirm Password
            </label>
            <div className="relative">
              <input
                className="w-full rounded-xl bg-neutral-50 border border-neutral-200 text-neutral-900 placeholder-neutral-400 focus:ring-2 focus:ring-primary/20 focus:border-primary p-3.5 pl-11 text-sm transition-all"
                id="confirmPassword"
                name="confirmPassword"
                placeholder="Confirm your password"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required={isSignup}
              />
              <span
                className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none"
                style={{ fontSize: '20px' }}
              >
                lock
              </span>
            </div>
          </div>
        )}

        <button
          className="w-full mt-3 h-12 bg-primary hover:bg-primary-600 text-white font-semibold rounded-xl shadow-md shadow-primary/25 hover:shadow-lg hover:shadow-primary/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          type="submit"
          disabled={loading}
        >
          {loading ? (
            <LoadingSpinner size="sm" />
          ) : (
            <>
              <span>{isSignup ? 'Create Account' : 'Sign In'}</span>
              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                arrow_forward
              </span>
            </>
          )}
        </button>
      </form>

      {/* Separator */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-neutral-200"></div>
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="px-4 bg-white text-neutral-400 font-medium">or continue with</span>
        </div>
      </div>

      {/* Google Sign-In Button */}
      <button
        onClick={handleGoogleSignIn}
        disabled={googleLoading || loading}
        className="w-full h-12 bg-white hover:bg-neutral-50 border border-neutral-200 text-neutral-700 font-medium rounded-xl shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
        type="button"
      >
        {googleLoading ? (
          <LoadingSpinner size="sm" />
        ) : (
          <>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19.6 10.2273C19.6 9.51816 19.5364 8.83634 19.4182 8.18179H10V12.0491H15.3818C15.15 13.2991 14.4455 14.3591 13.3864 15.0682V17.5773H16.6182C18.5091 15.8364 19.6 13.2727 19.6 10.2273Z" fill="#4285F4"/>
              <path d="M10 20C12.7 20 14.9636 19.1045 16.6182 17.5773L13.3864 15.0682C12.4909 15.6682 11.3455 16.0227 10 16.0227C7.39545 16.0227 5.19091 14.2636 4.40455 11.9H1.06364V14.4909C2.70909 17.7591 6.09091 20 10 20Z" fill="#34A853"/>
              <path d="M4.40455 11.9C4.20455 11.3 4.09091 10.6591 4.09091 10C4.09091 9.34091 4.20455 8.7 4.40455 8.1V5.50909H1.06364C0.386364 6.85909 0 8.38636 0 10C0 11.6136 0.386364 13.1409 1.06364 14.4909L4.40455 11.9Z" fill="#FBBC04"/>
              <path d="M10 3.97727C11.4682 3.97727 12.7864 4.48182 13.8227 5.47273L16.6909 2.60455C14.9591 0.990909 12.6955 0 10 0C6.09091 0 2.70909 2.24091 1.06364 5.50909L4.40455 8.1C5.19091 5.73636 7.39545 3.97727 10 3.97727Z" fill="#EA4335"/>
            </svg>
            <span>Continue with Google</span>
          </>
        )}
      </button>

      {/* Toggle Login/Signup */}
      <p className="mt-8 text-center text-sm text-neutral-500">
        {isSignup ? 'Already have an account? ' : "Don't have an account? "}
        <button
          type="button"
          onClick={() => {
            setIsSignup(!isSignup);
            setError(null);
            setFormData({ email: '', password: '', confirmPassword: '', displayName: '' });
          }}
          className="font-semibold text-primary hover:text-primary-600 hover:underline transition-colors"
        >
          {isSignup ? 'Sign in' : 'Create one'}
        </button>
      </p>
    </div>
  );
};

export default Login;
